import { useState, useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CheckCircle2 } from 'lucide-react'
import { FormationNameOptionData } from '@/types'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  entityId: string
  option: FormationNameOptionData | null
  onSuccess: () => void
}

export function FormationNameOptionSheet({ open, onOpenChange, entityId, option, onSuccess }: Props) {
  const isEditing = !!option
  const [success, setSuccess] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  const { data, setData, post, patch, processing, reset, clearErrors } = useForm({
    label: '',
    suffix: '',
    position: 0
  })

  useEffect(() => {
    if (open) {
      if (option) {
        setData({
          label: option.label,
          suffix: option.suffix,
          position: option.position
        })
      } else {
        reset()
      }
      setApiError(null)
      setSuccess(false)
      clearErrors()
    }
  }, [open, option])

  function handleClose(value: boolean) {
    if (!value) {
      setSuccess(false)
      setApiError(null)
    }
    onOpenChange(value)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setApiError(null)

    const url = isEditing
      ? `/backoffice/entities/${entityId}/formation_name_options/${option.id}`
      : `/backoffice/entities/${entityId}/formation_name_options`

    const request = isEditing ? patch : post

    request(url, {
      preserveScroll: true,
      onSuccess: () => {
        setSuccess(true)
        onSuccess()
        setTimeout(() => handleClose(false), 1000)
      },
      onError: (errors) => {
        const errorMessages = Object.values(errors).filter(Boolean)
        setApiError(errorMessages.length > 0 ? errorMessages.join(', ') : 'Something went wrong')
      }
    })
  }

  if (success) {
    return (
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent side="right" className="sm:max-w-lg">
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm font-medium text-foreground">
              {isEditing ? 'Name option updated' : 'Name option added'}
            </p>
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEditing ? 'Edit Name Option' : 'Add Name Option'}</SheetTitle>
          <SheetDescription>
            {isEditing
              ? `Update the "${option!.label}" name option`
              : 'Add a new allowed name suffix for this entity type'}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-5 px-4 mt-6">
          {apiError && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-lg border border-destructive/20">
              {apiError}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="label">Label <span className="text-destructive">*</span></Label>
            <Input
              id="label"
              value={data.label}
              onChange={e => setData('label', e.target.value)}
              placeholder="e.g. LLC"
              required
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="suffix">Suffix <span className="text-destructive">*</span></Label>
            <Input
              id="suffix"
              value={data.suffix}
              onChange={e => setData('suffix', e.target.value)}
              placeholder="e.g. LLC"
              required
            />
            <p className="text-[12px] text-muted-foreground">The actual legal suffix appended to the company name</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="position">Position</Label>
            <Input
              id="position"
              type="number"
              value={data.position}
              onChange={e => setData('position', parseInt(e.target.value) || 0)}
              required
            />
            <p className="text-[12px] text-muted-foreground">Ordering parameter (lower is earlier)</p>
          </div>

          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-8">
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={processing || !data.label || !data.suffix}>
                {processing ? 'Saving...' : isEditing ? 'Update Option' : 'Add Option'}
              </Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
